import { inject, Injectable } from '@angular/core';
import { API_URLS, WebServiceProvider } from '@pinnakl/core/web-services';
import { SecurityBase, SecurityPostPayload } from '@pinnakl/securities/domain';
import { DataService, Filter } from '@pinnakl/shared/signal-store';
import { SecuritiesSearchFilter, SecuritySearchResult } from '@pinnakl/shared/types';
import { getErrorMessage } from '@pinnakl/shared/util-helpers';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';

export type SecuritiesFilter = Filter;

@Injectable({
  providedIn: 'root',
})
export class SecuritiesApiService implements DataService<SecurityBase, SecuritiesFilter> {
  private readonly wsp = inject(WebServiceProvider);
  private readonly apiUrls = inject(API_URLS);
  private readonly toast = inject(PinnaklUIToastMessage);

  async loadById(id: number): Promise<SecurityBase> {
    try {
      return await this.findById(id);
    } catch (error) {
      this.toast.warning('Unable to load security.' + getErrorMessage('', error));
      throw error;
    }
  }

  async create(data: SecurityPostPayload): Promise<SecurityBase> {
    try {
      return await this.createEntity(data);
    } catch (error) {
      this.toast.warning('Unable to create security.' + getErrorMessage('', error));
      throw error;
    }
  }

  async updateById(id: number, data: SecurityBase): Promise<SecurityBase> {
    try {
      return await this.updateEntity(id, data);
    } catch (error) {
      this.toast.warning(`Unable to update security'}.` + getErrorMessage('', error));
      throw error;
    }
  }

  async removeById(id: number): Promise<void> {
    try {
      return await this.removeEntity(id);
    } catch (error) {
      this.toast.warning('Unable to delete security. ' + getErrorMessage('', error));
      throw error;
    }
  }

  public findById(id: number): Promise<SecurityBase> {
    return this.wsp.get<SecurityBase>({
      endpoint: this.apiUrls.securityV4,
      params: { id },
    });
  }

  public createEntity(body: SecurityPostPayload): Promise<SecurityBase> {
    return this.wsp
      .post<SecurityBase>({
        endpoint: this.apiUrls.securitiesV4,
        payload: body,
      })
      .then(value => {
        (() => {
          this.toast.success(`${body.ticker} has been created`);
        })();
        return value;
      });
  }

  public updateEntity(id: number, body: SecurityBase): Promise<SecurityBase> {
    return this.wsp
      .put<SecurityBase>({
        endpoint: this.apiUrls.securityV4,
        params: { id },
        payload: body,
      })
      .then(value => {
        (() => {
          this.toast.success(`${body.description} has been updated`);
        })();
        return value;
      });
  }

  async searchSecurities(otherParams?: SecuritiesSearchFilter): Promise<SecuritySearchResult[]> {
    return this.wsp.get<SecuritySearchResult[]>({
      endpoint: this.apiUrls.securitiesV4,
      queryParams: otherParams ? { ...otherParams } : undefined,
    });
  }

  async loadAffectedSecurityTradesAmount(securityId: number): Promise<{ affectedCount: 0 }> {
    try {
      return await this.wsp.get<{ affectedCount: 0 }>({
        endpoint: this.apiUrls.securityAffectedTrades,
        params: { securityId },
      });
    } catch (error) {
      this.toast.error('Unable to load affected security trades amount. ' + getErrorMessage('', error));
      throw error;
    }
  }

  private removeEntity(id: number): Promise<void> {
    return this.wsp.delete<void>({
      endpoint: this.apiUrls.securityV4,
      params: { id },
    });
  }
}
